import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Route prefixes where the tawk.to chat widget should NOT appear. These are
// logged-in workflows where an unsolicited chat bubble would just get in the
// way — signing pages, admin/lender/client portals, etc. Everything else
// (marketing, public quote landings, /listing-agents, /lenders/apply, etc.)
// gets the widget.
const HIDDEN_PREFIXES = [
  "/admin",
  "/portal",
  "/client",
  "/lender/portal",
  "/fee-agreement",   // Signing page — a chat overlay on top of the signature form is a bad UX
  "/adscopilot",
];

/**
 * tawk.to live-chat widget.
 *
 * Loads the tawk.to embed script on public marketing surfaces only. Reads
 * property + widget IDs from REACT_APP_TAWK_PROPERTY_ID / REACT_APP_TAWK_WIDGET_ID
 * so they can be swapped without code changes.
 *
 * Cleanup: on route change into a hidden path, we hide the widget via
 * Tawk_API.hideWidget() (their supported off-switch). We keep the loaded
 * script in place — a full teardown + re-load on every navigation would be
 * noisy in analytics and slow.
 */
export default function TawkChat() {
  const location = useLocation();
  const propertyId = process.env.REACT_APP_TAWK_PROPERTY_ID || "5dd41d3ed96992700fc83837";
  const widgetId = process.env.REACT_APP_TAWK_WIDGET_ID || "default";

  useEffect(() => {
    if (!propertyId || !widgetId) return;
    // Only inject once per page load. If the script is already on the page,
    // we skip the injection and simply toggle visibility below.
    const scriptId = "tawk-embed-script";
    if (!document.getElementById(scriptId)) {
      window.Tawk_API = window.Tawk_API || {};
      window.Tawk_LoadStart = new Date();
      const s = document.createElement("script");
      s.id = scriptId;
      s.async = true;
      s.src = `https://embed.tawk.to/${propertyId}/${widgetId}`;
      s.charset = "UTF-8";
      // tawk.to's CDN serves the script with content-type "application/x-javascript"
      // + "x-content-type-options: nosniff". Under Chrome's Opaque Response
      // Blocking (ORB), a no-cors <script> gets blocked. Requesting the
      // resource with CORS (crossorigin="anonymous") passes because the CDN
      // also returns Access-Control-Allow-Origin: * — which then bypasses ORB.
      s.crossOrigin = "anonymous";
      document.head.appendChild(s);
    }
  }, [propertyId, widgetId]);

  // Show/hide the widget on route change. We can't rely on unmount because
  // this component stays mounted at the top of <BrowserRouter>.
  useEffect(() => {
    const path = location.pathname || "/";
    const hidden = HIDDEN_PREFIXES.some((p) => path === p || path.startsWith(p + "/"));
    // Poll for the API to be ready — tawk.to's script loads async.
    let attempts = 0;
    const tick = () => {
      const api = window.Tawk_API;
      if (api && typeof api.showWidget === "function" && typeof api.hideWidget === "function") {
        try { hidden ? api.hideWidget() : api.showWidget(); } catch { /* ignore */ }
        return;
      }
      if (attempts++ < 30) setTimeout(tick, 200);   // give the widget up to 6s to load
    };
    tick();
  }, [location.pathname]);

  return null;
}
